const transientStatus = (status) => status === 408 || status === 429 || status >= 500;

export const isTransientTaxonomyFailure = (error) => (
  !error?.response || transientStatus(Number(error.response.status || 0))
);

export const normaliseTaxonomyNames = names => [...new Map(
  String(names || '')
    .split(/[,;\n\t]+/)
    .map(value => value.trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .map(value => [value.toLocaleLowerCase(), value]),
).values()];

const reconcileDocumentTypes = async (axiosClient, names) => {
  if (typeof axiosClient.get !== 'function') return null;
  try {
    const response = await axiosClient.get('/source-types', { timeout: 15000 });
    const existing = Array.isArray(response.data) ? response.data : [];
    const byName = new Map(existing.map(item => [String(item.name || '').trim().toLocaleLowerCase(), item]));
    const requested = normaliseTaxonomyNames(names);
    const recovered = requested.map(name => byName.get(name.toLocaleLowerCase())).filter(Boolean);
    if (requested.length && recovered.length === requested.length) {
      return { data: { document_types: recovered, created_count: 0, reconciled: true } };
    }
  } catch {
    // Preserve the original mutation failure; this read is best-effort recovery.
  }
  return null;
};

const reconcileKnowledgeBadges = async (axiosClient, names) => {
  if (typeof axiosClient.get !== 'function') return null;
  try {
    const response = await axiosClient.get('/knowledge-badges', { timeout: 15000 });
    const existing = Array.isArray(response.data) ? response.data : [];
    const byName = new Map(existing.map(item => [String(item.name || '').trim().toLocaleLowerCase(), item]));
    const requested = normaliseTaxonomyNames(names);
    const recovered = requested.map(name => byName.get(name.toLocaleLowerCase())).filter(Boolean);
    if (requested.length && recovered.length === requested.length) {
      return { data: { badges: recovered, created_count: 0, reconciled: true } };
    }
  } catch {
    // Preserve the original mutation failure; this read is best-effort recovery.
  }
  return null;
};

export const addDocumentTypesWithRetry = async (
  axiosClient,
  names,
  wait = () => new Promise(resolve => window.setTimeout(resolve, 650)),
) => {
  const requested = normaliseTaxonomyNames(names);
  if (!requested.length || requested.length > 100) {
    throw { response: { status: 400, data: { detail: 'Enter between 1 and 100 document types' } } }; // eslint-disable-line no-throw-literal
  }
  const tooLong = requested.find(name => name.length > 100);
  if (tooLong) {
    throw { response: { status: 400, data: { detail: `“${tooLong.slice(0, 80)}${tooLong.length > 80 ? '…' : ''}” is too long. Keep each document type to 100 characters.` } } }; // eslint-disable-line no-throw-literal
  }

  const submitOne = name => axiosClient.post('/source-types', { name }, { timeout: 30000 });
  const submitIndividually = async () => {
    const documentTypes = [];
    for (const name of requested) {
      try {
        const response = await submitOne(name);
        documentTypes.push(response.data);
      } catch (error) {
        if (Number(error?.response?.status || 0) === 409) {
          const recovered = await reconcileDocumentTypes(axiosClient, [name]);
          if (recovered?.data?.document_types?.[0]) {
            documentTypes.push(recovered.data.document_types[0]);
            continue;
          }
        }
        throw error;
      }
    }
    return { data: { document_types: documentTypes, created_count: documentTypes.length } };
  };

  // A single type is the common interactive path. Use the long-established
  // single-create endpoint so this control still works while older API
  // deployments or proxies do not yet expose the newer bulk route.
  if (requested.length === 1) {
    try {
      return await submitIndividually();
    } catch (error) {
      if (!isTransientTaxonomyFailure(error)) throw error;
      const recovered = await reconcileDocumentTypes(axiosClient, requested);
      if (recovered) return recovered;
      await wait();
      try {
        return await submitIndividually();
      } catch (retryError) {
        if (isTransientTaxonomyFailure(retryError)) {
          const recoveredAfterRetry = await reconcileDocumentTypes(axiosClient, requested);
          if (recoveredAfterRetry) return recoveredAfterRetry;
        }
        throw retryError;
      }
    }
  }

  const submitBulk = () => axiosClient.post('/source-types/bulk', { names: requested }, { timeout: 60000 });
  try {
    return await submitBulk();
  } catch (error) {
    const bulkStatus = Number(error?.response?.status || 0);
    const bulkUnavailable = isTransientTaxonomyFailure(error) || bulkStatus === 404 || bulkStatus === 405;
    if (!bulkUnavailable) throw error;
    const recovered = await reconcileDocumentTypes(axiosClient, requested);
    if (recovered) return recovered;
    await wait();
    try {
      return await submitIndividually();
    } catch (retryError) {
      if (isTransientTaxonomyFailure(retryError)) {
        const recoveredAfterRetry = await reconcileDocumentTypes(axiosClient, requested);
        if (recoveredAfterRetry) return recoveredAfterRetry;
      }
      throw retryError;
    }
  }
};

export const addKnowledgeBadgesWithRetry = async (
  axiosClient,
  names,
  color,
  wait = () => new Promise(resolve => window.setTimeout(resolve, 650)),
) => {
  const requested = normaliseTaxonomyNames(names);
  if (!requested.length || requested.length > 500) {
    throw { response: { status: 400, data: { detail: 'Enter between 1 and 500 badges/topics' } } }; // eslint-disable-line no-throw-literal
  }
  const tooLong = requested.find(name => name.length > 160);
  if (tooLong) {
    throw { response: { status: 400, data: { detail: `“${tooLong.slice(0, 80)}${tooLong.length > 80 ? '…' : ''}” is too long. Keep each badge/topic to 160 characters.` } } }; // eslint-disable-line no-throw-literal
  }
  const submit = () => axiosClient.post(
    '/knowledge-badges/bulk',
    { names: requested, color },
    { timeout: 60000 },
  );
  try {
    return await submit();
  } catch (error) {
    if (!isTransientTaxonomyFailure(error)) throw error;
    const recovered = await reconcileKnowledgeBadges(axiosClient, requested);
    if (recovered) return recovered;
    await wait();
    try {
      return await submit();
    } catch (retryError) {
      if (isTransientTaxonomyFailure(retryError)) {
        const recoveredAfterRetry = await reconcileKnowledgeBadges(axiosClient, requested);
        if (recoveredAfterRetry) return recoveredAfterRetry;
      }
      throw retryError;
    }
  }
};

export const classifyTaxonomyFailure = (error, fallback) => {
  const status = Number(error?.response?.status || 0);
  const detail = error?.response?.data?.detail;
  const message = typeof detail === 'string' ? detail : '';
  if (status === 401) {
    return {
      sessionRevoked: true,
      message: 'Your sign-in session changed or expired. Sign in again to continue.',
    };
  }
  if (status === 403) {
    return {
      sessionRevoked: false,
      message: message || 'Upload permission is required to manage upload metadata.',
    };
  }
  if (status === 413) {
    return {
      sessionRevoked: false,
      message: 'That pasted list is too large for one request. Split it into two smaller lists and try again.',
    };
  }
  if (!error?.response) {
    return {
      sessionRevoked: false,
      message: 'Qflow could not reach the service after retrying. Your entry is still here—please try again.',
    };
  }
  if (status >= 500) {
    return {
      sessionRevoked: false,
      message: 'Qflow could not save the document types after retrying. Please try again shortly.',
    };
  }
  return { sessionRevoked: false, message: message || fallback };
};
